import { useCallback, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InventoryTab from '../../../components/tabs/InventoryTab';
import Button from '../../../components/base/Button';
import type { Domain } from '@shared/types/project.type';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '90vw', md: 720 },
  maxWidth: 720,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
  overflow: 'auto'
};

export interface MandatoryPolicyUploadModalProps {
  open: boolean;
  projectId: string;
  projectTitle: string;
  accountDomain: Domain | null;
  onContinue: () => void;
}

const MandatoryPolicyUploadModal = ({
  open,
  projectId,
  projectTitle,
  accountDomain,
  onContinue
}: MandatoryPolicyUploadModalProps) => {
  const [policyUploadCount, setPolicyUploadCount] = useState(0);

  const projectForUpload = useMemo(
    () =>
      accountDomain
        ? {
            projectId,
            domain: accountDomain
          }
        : undefined,
    [accountDomain, projectId]
  );

  const handlePolicyUploaded = useCallback(() => {
    setPolicyUploadCount(c => c + 1);
  }, []);

  const handleContinue = () => {
    if (policyUploadCount < 1 || !accountDomain) return;
    onContinue();
  };

  return (
    <Modal open={open} disableEscapeKeyDown aria-labelledby="mandatory-policy-upload-title">
      <Box sx={modalStyle}>
        <Typography id="mandatory-policy-upload-title" variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Upload policy documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Project <strong>{projectTitle}</strong> was created. Upload at least one policy document (PDF or DOCX) to
          continue. This step is required.
          {accountDomain ? (
            <>
              {' '}
              Using your account domain: <strong>{accountDomain}</strong>.
            </>
          ) : null}
        </Typography>

        {!accountDomain && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Your account domain is not set. Sign out and sign in again, or contact your administrator.
          </Alert>
        )}

        {accountDomain && projectForUpload && (
          <InventoryTab
            uploadMode="policy-only"
            projectForUpload={projectForUpload}
            onSuccessfulPolicyUpload={handlePolicyUploaded}
          />
        )}

        <Typography variant="body2" sx={{ mt: 2, color: policyUploadCount > 0 ? 'success.main' : 'warning.main' }}>
          {policyUploadCount > 0
            ? `${policyUploadCount} policy document(s) uploaded. You can add more or continue.`
            : 'No policy document uploaded yet.'}
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="phoenix-primary"
            className="px-4"
            onClick={handleContinue}
            disabled={!accountDomain || policyUploadCount < 1}
          >
            Continue to project
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MandatoryPolicyUploadModal;
